import {Event as Event_V3} from '@wix/ambassador-events-v3-event/types'
import {prependHTTP} from '@wix/panda-js-utils'
import {
  getEventBadges,
  getExternalLink,
  isEventDetailsPageHidden,
  isEventDetailsPageHiddenInternal,
  isRecurringEvent,
  isRegistrationClosed,
  isRestrictedToMembers,
  isRsvp,
  isSoldOut,
  isWaitlistOpen,
} from '@wix/wix-events-commons-statics'
import type {TFunction} from 'react-i18next'
import {RIBBON} from '../../../../commons/constants/ribbons'
import {isMobile, isSite} from '../../../../commons/selectors/environment'
import {getDetailsTarget} from '../../../../commons/services/navigation'
import {WidgetComponentTextSettings} from '../../settingsParams'
import {AppProps} from '../types/props'
import {State} from '../types/state'
import {DEMO_EVENT_BADGES} from '../utils/event-badges'
import {getEventBadgeByPriority, getEventHighestPriorityBadge} from './event-badges'
import {getForcedEventBadge, getForcedRibbon, shouldForceShowRibbon} from './layout'
import {
  getComponentConfig,
  getWidgetType,
  isJoinWaitlistRibbonEnabled,
  isOfferRibbonEnabled,
  isOfferRibbonVisible,
  isRecurringRibbonEnabled,
  isRibbonEnabled,
  isRibbonVisible,
  isRsvpClosedRibbonEnabled,
  isSoldOutRibbonEnabled,
} from './settings'
import {isOwner} from './user'

export const getEventById = (state: State, eventId: string) => getEvents(state).find(event => event.id === eventId)

export const getEvents = (state: State): (ExtendedEvent | Event_V3)[] => {
  if (shouldShowRealEvents(state)) {
    return getRealEvents(state)
  }

  if (shouldShowDemoEvents(state)) {
    return getDemoEvents(state)
  }

  return []
}

export const hadEventsEver = (state: State) => state.siteSettings.firstEventCreated || hasRealEvents(state)

export const shouldShowDemoEvents = (state: State) =>
  (hadEventsEver(state) && !isSite(state) && !hasRealEvents(state)) ||
  (!hadEventsEver(state) && (isOwner(state) || !isSite(state)))

export const shouldShowRealEvents = (state: State) => hadEventsEver(state) && (isSite(state) || hasRealEvents(state))

export const getRealEvents = (state: State) => state.events.events
export const getRealEventsCount = (state: State) => getRealEvents(state).length
export const hasRealEvents = (state: State) => !!getRealEventsCount(state)

export const getDemoEvents = (state: State) =>
  (state.demoEvents[getWidgetType(getComponentConfig(state))] as ExtendedEvent[]) || []

export const getFirstEvent = (state: State) => getEvents(state)[0]

export const getEventsIds = (state: State) => getEvents(state).map(event => event.id)

const RIBBONS: {[key in RIBBON]: RibbonData} = {
  [RIBBON.SOLD_OUT]: {
    visible: true,
    ribbon: RIBBON.SOLD_OUT,
    defaultText: 'ribbon.soldOut',
    textProp: 'soldOutRibbonText',
    status: true,
  },
  [RIBBON.RSVP_CLOSED]: {
    visible: true,
    ribbon: RIBBON.RSVP_CLOSED,
    defaultText: 'ribbon.rsvpClosed',
    textProp: 'rsvpClosedRibbonText',
    status: true,
  },
  [RIBBON.JOIN_WAITLIST]: {
    visible: true,
    ribbon: RIBBON.JOIN_WAITLIST,
    defaultText: 'ribbon.joinWaitList',
    textProp: 'joinWaitlistRibbonText',
    status: true,
  },
  [RIBBON.MEMBERSHIP]: {
    visible: true,
    ribbon: RIBBON.MEMBERSHIP,
    defaultText: 'ribbon.memberOffer',
    textProp: 'membershipRibbonText',
    status: false,
  },
  [RIBBON.RECURRING]: {
    visible: true,
    ribbon: RIBBON.RECURRING,
    defaultText: 'ribbon.recurringDates',
    textProp: 'recurringRibbonText',
    status: false,
  },
}

interface RibbonData {
  visible: boolean
  ribbon?: RIBBON
  defaultText?: string
  textProp?: keyof WidgetComponentTextSettings
  status?: boolean
}

export const getRibbonData = (state: State, event: ExtendedEvent | Event_V3): RibbonData => {
  if (!isRibbonVisible(state, event)) {
    return {visible: false}
  }

  if (shouldForceShowRibbon(state, event)) {
    return getShowcaseRibbonData(state)
  }

  const componentConfig = getComponentConfig(state)
  const mobile = isMobile(state)

  let ribbon: RibbonData = {visible: false}
  if (isRecurringRibbonEnabled(componentConfig, mobile) && isRecurringEvent(event)) {
    ribbon = RIBBONS[RIBBON.RECURRING]
  } else if (isSoldOutRibbonEnabled(componentConfig, mobile) && isSoldOut(event)) {
    ribbon = RIBBONS[RIBBON.SOLD_OUT]
  } else if (isRsvpClosedRibbonEnabled(componentConfig, mobile) && isRegistrationClosed(event)) {
    ribbon = RIBBONS[RIBBON.RSVP_CLOSED]
  } else if (isJoinWaitlistRibbonEnabled(componentConfig, mobile) && isRsvp(event) && isWaitlistOpen(event)) {
    ribbon = RIBBONS[RIBBON.JOIN_WAITLIST]
  } else if (isOfferRibbonEnabled(componentConfig, mobile) && isOfferRibbonVisible(state, event)) {
    ribbon = RIBBONS[RIBBON.MEMBERSHIP]
  }

  return ribbon
}

const getShowcaseRibbonData = (state: State): RibbonData => {
  const componentConfig = getComponentConfig(state)
  const mobile = isMobile(state)
  const ribbon = getForcedRibbon(state)
  if (ribbon && isRibbonEnabled(ribbon, componentConfig, mobile)) {
    return RIBBONS[ribbon]
  }
  return {
    visible: false,
  }
}

export const isEventRestricted = (state: State, event: ExtendedEvent | Event_V3) =>
  isEventDetailsPageHiddenInternal(event) && isRsvp(event) && isRestrictedToMembers(event) && !state.memberLoggedIn

export const hasEventDetailsUrl = (state: State, event: ExtendedEvent | Event_V3) =>
  isEventDetailsPageHidden(event) || (isSite(state) && !isEventRestricted(state, event))

export const getEventUrl = (
  state: State,
  event: ExtendedEvent | Event_V3,
  baseUrl: string,
  pageUrl: AppProps['pageUrl'],
  builderPageUrl: AppProps['builderPageUrl'],
  queryParams: string,
) => {
  if (isEventDetailsPageHidden(event)) {
    return prependHTTP(getExternalLink(event))
  }
  return getEventDetailsPageUrl(state, event, baseUrl, pageUrl, builderPageUrl, queryParams)
}

export const getEventDetailsPageUrl = (
  state: State,
  event: ExtendedEvent | Event_V3,
  baseUrl: string,
  pageUrl: AppProps['pageUrl'],
  builderPageUrl: AppProps['builderPageUrl'],
  queryParams: string,
) => {
  const detailsPageEnabled = !isEventDetailsPageHiddenInternal(event)
  const url = builderPageUrl && detailsPageEnabled ? builderPageUrl : pageUrl

  if (isSite(state) && url?.relativeUrl && !isEventRestricted(state, event)) {
    return `${baseUrl}${url.relativeUrl}/${getDetailsTarget(event, detailsPageEnabled)}${queryParams}`
  }

  return null
}

export const hasMoreEvents = (state: State) => state.events.hasMore
export const isMoreEventsLoading = (state: State) => state.events.moreLoading

export const getSelectedEvents = (state: State) => getEventsIds(state)

export const getMultiDayText = (state: State, id: string, currentDate: string, t: TFunction) => {
  const event = state.dates.events[id]
  if (!event.multiDayInfo) {
    return ''
  }

  const {duration, ...eventDates} = event.multiDayInfo
  const currentDay = eventDates[currentDate]

  return t('layout.calendar.monthly.events.multiDayTitle', {count: currentDay, total: duration})
}

export const getEventBadge = (state: State, event: ExtendedEvent | Event_V3) => {
  const forcedEventBadge = getForcedEventBadge(state)
  const demoEventBadge = DEMO_EVENT_BADGES[forcedEventBadge]

  return forcedEventBadge
    ? (getEventBadgeByPriority(getEventBadges(event) || [], demoEventBadge.type) ?? demoEventBadge)
    : getEventHighestPriorityBadge(getEventBadges(event) || [])
}
